import { useState } from 'react';
import { ArrowLeft, AlertCircle, Shield, Eye } from 'lucide-react';
import { NavigationBar } from "./shared/Navigation";
import { Footer } from "./shared/Footer";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Separator } from "./ui/separator";
import { toast } from "sonner";
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/api';

// Components
import { CarSummary } from "./buyingForm/CarSummary";
import { FormSteps } from "./buyingForm/FormSteps";
import { FileUpload } from "./buyingForm/FileUpload";
import { ReviewSection } from "./buyingForm/ReviewSection";
import { BuyingFormSuccessModal } from "./buyingForm/BuyingFormSuccessModal";

// Constants and utilities
import { COUNTRY_OPTIONS, USA_STATES, CANADA_PROVINCES } from "../constants/buyingForm";
import { getInitialFormData, canSubmitForm } from "../utils/buyingFormHelpers";
import type { BuyingFormProps, BuyingFormData } from "../types/buyingForm";

export default function BuyingForm({
  car,
  onNavigate,
  onBackToDetail,
  onContactClick,
  onOpenTerms,
  onOpenPrivacy
}: BuyingFormProps) {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<BuyingFormData>(getInitialFormData());
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedApplicationId, setSubmittedApplicationId] = useState<number | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Record<string, File | null>>({
    'photo-id': null,
    'proof-of-income': null,
    'bank-statements': null,
    'proof-of-residence': null,
    'references': null
  });

  // Create translated options
  const employmentStatusOptions = [
    { value: "employed", label: t('buyingForm.employed') },
    { value: "self-employed", label: t('buyingForm.selfEmployed') },
    { value: "unemployed", label: t('buyingForm.unemployed') },
    { value: "retired", label: t('buyingForm.retired') },
    { value: "student", label: t('buyingForm.student') }
  ];

  const creditScoreOptions = [
    { value: "excellent", label: t('buyingForm.excellent') },
    { value: "good", label: t('buyingForm.good') },
    { value: "fair", label: t('buyingForm.fair') },
    { value: "poor", label: t('buyingForm.poor') },
    { value: "bad", label: t('buyingForm.bad') }
  ];

  const documentTypes = [
    {
      key: 'photo-id',
      label: t('buyingForm.photoId'),
      accept: ".pdf,.jpg,.jpeg,.png",
      required: true
    },
    {
      key: 'proof-of-income',
      label: t('buyingForm.proofOfIncome'),
      accept: ".pdf,.jpg,.jpeg,.png",
      required: true
    },
    {
      key: 'bank-statements',
      label: t('buyingForm.bankStatements'),
      accept: ".pdf,.jpg,.jpeg,.png",
      required: true
    },
    {
      key: 'proof-of-residence',
      label: t('buyingForm.proofOfResidence'),
      accept: ".pdf,.jpg,.jpeg,.png",
      required: false
    },
    {
      key: 'references',
      label: t('buyingForm.references'),
      accept: ".pdf,.jpg,.jpeg,.png",
      required: false
    }
  ];

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'country') {
        next.state = '';
      }
      return next;
    });
  };

  const stateProvinceOptions = formData.country === 'Canada' ? CANADA_PROVINCES : USA_STATES;

  const handleFileSelect = (documentKey: string, file: File | null) => {
    console.log(`BuyingForm: File selected for ${documentKey}:`, file?.name);
    setSelectedFiles(prev => {
      const updated = {
        ...prev,
        [documentKey]: file
      };
      console.log('BuyingForm: Updated selectedFiles:', Object.entries(updated).map(([key, file]) => ({ key, fileName: file?.name })));
      return updated;
    });
  };

  const uploadSelectedFiles = async (applicationId: number) => {
    console.log('BuyingForm: Starting file upload for applicationId:', applicationId);
    console.log('BuyingForm: Current selectedFiles state:', Object.entries(selectedFiles).map(([key, file]) => ({ key, fileName: file?.name, hasFile: !!file })));
    
    const filesToUpload = Object.entries(selectedFiles).filter(([_, file]) => file !== null);
    console.log('BuyingForm: Files to upload:', filesToUpload.map(([key, file]) => ({ key, fileName: file?.name })));
    
    if (filesToUpload.length === 0) {
      console.log('BuyingForm: No files to upload');
      return [];
    }

    const uploadPromises = filesToUpload.map(async ([documentKey, file]) => {
        try {
          console.log(`BuyingForm: Uploading ${documentKey} (${file?.name}) to application ${applicationId}`);
          const result = await apiService.uploadApplicationDocument(
            applicationId,
            file!,
            documentKey
          );
          console.log(`BuyingForm: Successfully uploaded ${documentKey}:`, result);
          return { documentKey, success: true, result };
        } catch (error) {
          console.error(`BuyingForm: Failed to upload ${documentKey}:`, error);
          return { documentKey, success: false, error };
        }
      });

    const results = await Promise.all(uploadPromises);
    const successful = results.filter(r => r.success);
    const failed = results.filter(r => !r.success);

    console.log('BuyingForm: Upload results:', { successful: successful.length, failed: failed.length, results });

    if (successful.length > 0) {
      toast.success(`${successful.length} document(s) uploaded successfully`);
    }
    if (failed.length > 0) {
      toast.error(`${failed.length} document(s) failed to upload`);
    }

    return results;
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmitForm(formData)) {
      toast.error(t('buyingForm.authError'));
      return;
    }
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Transform form data to API format
      const applicationData = {
        car_listing_id: car?.id || undefined,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        income: formData.monthlyIncome ? parseFloat(formData.monthlyIncome.replace(/[^0-9.]/g, '')) * 12 : undefined, // Convert monthly to annual
        employment_status: formData.employmentStatus,
        credit_score_range: formData.creditScore,
        down_payment: formData.downPayment ? parseFloat(formData.downPayment.replace(/[^0-9.]/g, '')) : undefined,
        loan_amount: car?.totalPrice ? car.totalPrice - (parseFloat(formData.downPayment?.replace(/[^0-9.]/g, '') || '0')) : undefined,
        loan_term: 60, // Default to 60 months
        notes: `
          Address: ${formData.address}, ${formData.city}, ${formData.state || formData.country ? [formData.state, formData.country].filter(Boolean).join(', ') : ''} ${formData.zipCode}
          Country: ${formData.country || 'Not provided'}
          State/Province: ${formData.state || 'Not provided'}
          Employment: ${formData.employer} - ${formData.jobTitle}
          Existing Debts: ${formData.existingDebts}
          References: ${formData.references}
          Emergency Contact: ${formData.emergencyContact}
        `.trim()
      };

      console.log('Submitting purchase application:', applicationData);
      
      const result = await apiService.submitApplication(applicationData);
      console.log('Application submitted successfully:', result);
      
      // Store the application ID for file uploads
      const applicationId = result.id;
      setSubmittedApplicationId(applicationId);
      
      // Upload selected files if any
      const hasFiles = Object.values(selectedFiles).some(file => file !== null);
      console.log('BuyingForm: Checking for files to upload:', { hasFiles, applicationId, selectedFilesCount: Object.values(selectedFiles).filter(f => f !== null).length });
      if (hasFiles && applicationId) {
        console.log('BuyingForm: Uploading selected files...');
        await uploadSelectedFiles(applicationId);
      } else {
        console.log('BuyingForm: No files to upload or no application ID');
      }
      
      toast.success(t('buyingForm.submitSuccess'));
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(t('buyingForm.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!car) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationBar 
          onContactClick={onContactClick} 
        />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <AlertCircle className="mx-auto text-gray-400 mb-4" size={64} />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('buyingForm.noCarSelected')}</h1>
          <p className="text-gray-600 mb-8">{t('buyingForm.noCarMessage')}</p>
          <Button onClick={() => onNavigate('listings')} className="bg-[#f91bdc] hover:bg-[#e015c9]">
            {t('buyingForm.browseCars')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <NavigationBar
        onContactClick={onContactClick} 
      />
      
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button 
            variant="ghost" 
            onClick={onBackToDetail}
            className="text-[#f91bdc] hover:text-[#e015c9] hover:bg-[#f91bdc]/10"
          >
            <ArrowLeft size={20} className="mr-2" />
            {t('buyingForm.backToDetails')}
          </Button>
        </div>
      </div>

      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('buyingForm.purchaseForm').split(' ').map((word, index) => 
                word === 'Form' || word === 'Formulaire' ? (
                  <span key={index} className="text-[#f91bdc]">{word}</span>
                ) : (
                  <span key={index}>{word} </span>
                )
              )}
            </h1>
            <p className="text-xl text-gray-600">
              {t('buyingForm.subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          <div className="lg:col-span-2 mb-8 lg:mb-0">
            <Card>
              <CardContent className="p-6">
                <FormSteps currentStep={currentStep} />
                
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('buyingForm.step1')}</h2>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="firstName">{t('buyingForm.firstName')} *</Label>
                          <Input
                            id="firstName"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            className="mt-1"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="lastName">{t('buyingForm.lastName')} *</Label>
                          <Input
                            id="lastName"
                            value={formData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            className="mt-1"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">{t('buyingForm.email')} *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className="mt-1"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone">{t('buyingForm.phone')} *</Label>
                          <Input
                            id="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            className="mt-1"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="dateOfBirth">{t('buyingForm.dateOfBirth')} *</Label>
                          <Input
                            id="dateOfBirth"
                            type="date"
                            value={formData.dateOfBirth}
                            onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                            className="mt-1"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('buyingForm.addressInfo')}</h3>
                      <div className="grid gap-4">
                        <div>
                          <Label htmlFor="address">{t('buyingForm.streetAddress')} *</Label>
                          <Input
                            id="address"
                            value={formData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            className="mt-1"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="country">{t('buyingForm.country')} *</Label>
                          <Select value={formData.country} onValueChange={(value) => handleInputChange('country', value)}>
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder={t('buyingForm.selectCountry')} />
                            </SelectTrigger>
                            <SelectContent>
                              {COUNTRY_OPTIONS.map(option => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="city">{t('buyingForm.city')} *</Label>
                            <Input
                              id="city"
                              value={formData.city}
                              onChange={(e) => handleInputChange('city', e.target.value)}
                              className="mt-1"
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="state">{formData.country === 'Canada' ? t('buyingForm.province') : t('buyingForm.state')} *</Label>
                            <Select
                              value={formData.state}
                              onValueChange={(value) => handleInputChange('state', value)}
                              disabled={!formData.country}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder={formData.country ? (formData.country === 'Canada' ? t('buyingForm.selectProvince') : t('buyingForm.selectState')) : t('buyingForm.selectCountryFirst')} />
                              </SelectTrigger>
                              <SelectContent>
                                {stateProvinceOptions.map(option => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="zipCode">{t('buyingForm.zipCode')} *</Label>
                            <Input
                              id="zipCode"
                              value={formData.zipCode}
                              onChange={(e) => handleInputChange('zipCode', e.target.value)}
                              className="mt-1"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('buyingForm.financialInfo')}</h2>
                      <div className="grid gap-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="employmentStatus">{t('buyingForm.employmentStatus')} *</Label>
                            <Select value={formData.employmentStatus} onValueChange={(value) => handleInputChange('employmentStatus', value)}>
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder={t('buyingForm.selectStatus')} />
                              </SelectTrigger>
                              <SelectContent>
                                {employmentStatusOptions.map(option => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="monthlyIncome">{t('buyingForm.monthlyIncome')} *</Label>
                            <Input
                              id="monthlyIncome"
                              type="number"
                              value={formData.monthlyIncome}
                              onChange={(e) => handleInputChange('monthlyIncome', e.target.value)}
                              className="mt-1"
                              placeholder="$0"
                              required
                            />
                          </div>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="employer">{t('buyingForm.employer')}</Label>
                            <Input
                              id="employer"
                              value={formData.employer}
                              onChange={(e) => handleInputChange('employer', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="jobTitle">{t('buyingForm.jobTitle')}</Label>
                            <Input
                              id="jobTitle"
                              value={formData.jobTitle}
                              onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="creditScore">{t('buyingForm.creditScore')}</Label>
                            <Select value={formData.creditScore} onValueChange={(value) => handleInputChange('creditScore', value)}>
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder={t('buyingForm.selectRange')} />
                              </SelectTrigger>
                              <SelectContent>
                                {creditScoreOptions.map(option => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="downPayment">{t('buyingForm.downPayment')}</Label>
                            <Input
                              id="downPayment"
                              type="number"
                              value={formData.downPayment}
                              onChange={(e) => handleInputChange('downPayment', e.target.value)}
                              className="mt-1"
                              placeholder="$0"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="existingDebts">{t('buyingForm.existingDebts')}</Label>
                          <Textarea
                            id="existingDebts"
                            value={formData.existingDebts}
                            onChange={(e) => handleInputChange('existingDebts', e.target.value)}
                            className="mt-1"
                            placeholder={t('buyingForm.existingDebtsPlaceholder')}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('buyingForm.requiredDocuments')}</h2>
                      <p className="text-gray-600 mb-6">
                        {t('buyingForm.documentsMessage')}
                      </p>
                      
                      <div className="space-y-6">
                        {documentTypes.map((doc) => (
                          <FileUpload 
                            key={doc.key}
                            label={doc.label}
                            accept={doc.accept}
                            required={doc.required}
                            applicationId={submittedApplicationId || undefined}
                            documentType={doc.key}
                            onFileSelect={(file) => handleFileSelect(doc.key, file)}
                            onUploadComplete={(fileData) => {
                              console.log(`Document ${doc.key} uploaded:`, fileData);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 4 && (
                  <div className="space-y-6">
                    <div className="flex items-center space-x-2 mb-6">
                      <Eye className="text-[#f91bdc]" size={24} />
                      <h2 className="text-xl font-semibold text-gray-900">{t('buyingForm.reviewInfo')}</h2>
                    </div>
                    
                    <ReviewSection formData={formData} car={car} />
                    
                    <Separator />
                    
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('buyingForm.requiredAuth')}</h3>
                      <p className="text-gray-600 mb-6">
                        {t('buyingForm.authMessage')}
                      </p>
                      
                      <div className="space-y-4">
                        <div className="flex items-start space-x-3">
                          <Checkbox 
                            id="termsAccepted"
                            checked={formData.termsAccepted}
                            onCheckedChange={(checked) => handleInputChange('termsAccepted', checked)}
                          />
                          <div className="text-sm">
                            <label htmlFor="termsAccepted" className="cursor-pointer">
                              {t('buyingForm.termsAcceptanceText')}{' '}
                              <button 
                                type="button"
                                onClick={onOpenTerms}
                                className="text-[#f91bdc] hover:text-[#e015c9] underline"
                              >
                                {t('buyingForm.termsAndConditions')}
                              </button>
                              .
                            </label>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <Checkbox 
                            id="softCreditCheckAccepted"
                            checked={formData.softCreditCheckAccepted}
                            onCheckedChange={(checked) => handleInputChange('softCreditCheckAccepted', checked)}
                          />
                          <div className="text-sm">
                            <label htmlFor="softCreditCheckAccepted" className="cursor-pointer">
                              {t('buyingForm.softCreditAuth')}
                            </label>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <Checkbox 
                            id="hardCreditCheckAccepted"
                            checked={formData.hardCreditCheckAccepted}
                            onCheckedChange={(checked) => handleInputChange('hardCreditCheckAccepted', checked)}
                          />
                          <div className="text-sm">
                            <label htmlFor="hardCreditCheckAccepted" className="cursor-pointer">
                              {t('buyingForm.hardCreditAuth')}
                            </label>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <Checkbox 
                            id="dataConsentAccepted"
                            checked={formData.dataConsentAccepted}
                            onCheckedChange={(checked) => handleInputChange('dataConsentAccepted', checked)}
                          />
                          <div className="text-sm">
                            <label htmlFor="dataConsentAccepted" className="cursor-pointer">
                              {t('buyingForm.dataConsentText')}{' '}
                              <button 
                                type="button"
                                onClick={onOpenPrivacy}
                                className="text-[#f91bdc] hover:text-[#e015c9] underline"
                              >
                                {t('buyingForm.privacyPolicy')}
                              </button>
                              .
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6 p-4 bg-[#f91bdc]/5 rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <Shield className="text-[#f91bdc]" size={20} />
                          <span className="font-semibold text-gray-900">{t('buyingForm.securePurchase')}</span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {t('buyingForm.securityMessage')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-between pt-6 border-t">
                  <Button 
                    variant="outline" 
                    onClick={handlePrevious}
                    disabled={currentStep === 1}
                  >
                    {t('buyingForm.previous')}
                  </Button>
                  
                  {currentStep < 4 ? (
                    <Button 
                      onClick={handleNext}
                      className="bg-[#f91bdc] hover:bg-[#e015c9]"
                    >
                      {t('buyingForm.next')}
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleSubmit}
                      disabled={!canSubmitForm(formData) || isSubmitting}
                      className="bg-[#f91bdc] hover:bg-[#e015c9] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          {t('buyingForm.submitting')}
                        </>
                      ) : (
                        t('buyingForm.submit')
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="lg:col-span-1">
            <CarSummary car={car} />
          </div>
        </div>
      </div>

      <Footer 
        onOpenTerms={onOpenTerms} 
        onOpenPrivacy={onOpenPrivacy} 
      />

      {/* Success Modal */}
      <BuyingFormSuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          onNavigate('home');
        }}
        car={car}
        onBrowseMore={() => {
          setShowSuccessModal(false);
          onNavigate('listings');
        }}
      />
    </div>
  );
}